import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const PREFIX = 'ar_field_draft:';
const MAX_AGE = 24 * 60 * 60 * 1000;
const ALLOWED_INPUTS = new Set(['text', 'email', 'tel', 'url', 'number', 'date', 'time', 'month', 'datetime-local']);

const isEditable = (element) => {
  if (!(element instanceof HTMLElement) || element.disabled || element.readOnly) return false;
  if (element.dataset.draftIgnore !== undefined || element.closest('[data-draft-ignore]')) return false;
  if (element instanceof HTMLTextAreaElement) return true;
  return element instanceof HTMLInputElement && ALLOWED_INPUTS.has(element.type || 'text');
};

const fieldIdentity = (element) => {
  const form = element.closest('form');
  const formKey = form?.dataset?.draftKey || form?.getAttribute('name') || form?.id || 'page';
  const fieldKey = element.dataset.draftKey || element.getAttribute('name') || element.id || element.getAttribute('aria-label') || element.getAttribute('placeholder');
  return fieldKey ? `${formKey}:${fieldKey}` : null;
};

const setNativeValue = (element, value) => {
  const prototype = element instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(prototype, 'value')?.set;
  if (setter) setter.call(element, value); else element.value = value;
  element.dispatchEvent(new Event('input', { bubbles: true }));
  element.dispatchEvent(new Event('change', { bubbles: true }));
};

/**
 * Preserves unfinished text without showing update prompts. It is mounted once
 * by Layout so every operational module receives the same protection.
 */
export default function InteractionSafety({ scope = 'general', userId = 'anonymous' }) {
  const location = useLocation();

  useEffect(() => {
    const route = location.pathname;
    const storageKey = `${PREFIX}${userId}:${scope}:${route}`;
    let draft = {};
    let saveTimer;
    try {
      const cached = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
      if (cached?.updatedAt && Date.now() - cached.updatedAt < MAX_AGE) draft = cached.fields || {};
      else if (cached) sessionStorage.removeItem(storageKey);
    } catch (_) { sessionStorage.removeItem(storageKey); }

    const persist = () => {
      window.clearTimeout(saveTimer);
      saveTimer = window.setTimeout(() => {
        const fields = Object.fromEntries(Object.entries(draft).filter(([, value]) => String(value || '').trim()));
        if (Object.keys(fields).length) sessionStorage.setItem(storageKey, JSON.stringify({ updatedAt: Date.now(), fields }));
        else sessionStorage.removeItem(storageKey);
        window.__arHasUnsavedWork = Object.keys(fields).length > 0;
      }, 250);
    };

    const capture = (event) => {
      const element = event.target;
      if (!isEditable(element)) return;
      const identity = fieldIdentity(element);
      if (!identity) return;
      draft[identity] = element.value;
      persist();
    };

    const restore = (root = document) => {
      root.querySelectorAll?.('textarea, input').forEach((element) => {
        if (!isEditable(element) || String(element.value || '').trim()) return;
        const identity = fieldIdentity(element);
        if (identity && String(draft[identity] || '').trim()) setNativeValue(element, draft[identity]);
      });
    };

    const clearSubmitted = (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      form.querySelectorAll('textarea, input').forEach((element) => {
        const identity = fieldIdentity(element);
        if (identity) delete draft[identity];
      });
      persist();
    };

    const clearPageDraft = () => {
      draft = {};
      sessionStorage.removeItem(storageKey);
      window.__arHasUnsavedWork = false;
    };

    restore();
    const observer = new MutationObserver((entries) => entries.forEach((entry) => entry.addedNodes.forEach((node) => {
      if (node instanceof HTMLElement) {
        if (isEditable(node) && !String(node.value || '').trim()) {
          const identity = fieldIdentity(node);
          if (identity && String(draft[identity] || '').trim()) setNativeValue(node, draft[identity]);
        }
        restore(node);
      }
    })));
    observer.observe(document.body, { childList: true, subtree: true });
    document.addEventListener('input', capture, true);
    document.addEventListener('change', capture, true);
    document.addEventListener('submit', clearSubmitted, true);
    window.addEventListener('ar-clear-field-drafts', clearPageDraft);
    window.__arHasUnsavedWork = Object.values(draft).some((value) => String(value || '').trim());
    return () => {
      window.clearTimeout(saveTimer);
      observer.disconnect();
      document.removeEventListener('input', capture, true);
      document.removeEventListener('change', capture, true);
      document.removeEventListener('submit', clearSubmitted, true);
      window.removeEventListener('ar-clear-field-drafts', clearPageDraft);
    };
  }, [location.pathname, scope, userId]);

  return null;
}
